import {refreshSaleConsumers} from '@/lib/refreshSaleConsumers';
// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — useStaffMembers hook
// ─────────────────────────────────────────────────────────────────────────────
// Staff Members are non-login sellers (e.g. part-time floor staff) recorded on
// a sale alongside — never instead of — registered profiles. Managed via the
// "Staff" tab in Master Config (owner/admin only, nav-guarded). Read access is
// open to all authenticated org members, matching product_types/teams RLS.
// FEAT-SALE-ENH-001.
// ─────────────────────────────────────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@klothiq/domain';

export type StaffMember = Database['public']['Tables']['staff_members']['Row'];

export const STAFF_MEMBERS_KEY = 'staff_members';

// ── Active staff, for the Sale Form Seller dropdown ────────────────────────
export function useStaffMembers() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [STAFF_MEMBERS_KEY, 'active', orgId],
    queryFn: async (): Promise<StaffMember[]> => {
      const { data, error } = await supabase
        .from('staff_members')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!orgId,
    staleTime: 1000 * 60 * 5, // 5 min — matches useOrgProfiles
  });
}

// ── All staff (active + inactive), for the Master Config management tab ────
export function useAllStaffMembers() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [STAFF_MEMBERS_KEY, 'all', orgId],
    queryFn: async (): Promise<StaffMember[]> => {
      const { data, error } = await supabase
        .from('staff_members')
        .select('*')
        .eq('organization_id', orgId!)
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!orgId,
  });
}

export function useCreateStaffMember() {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();

  return useMutation({
    mutationFn: async (values: { name: string; role_label?: string }) => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('Organization not found.');

      const { data, error } = await supabase
        .from('staff_members')
        .insert({
          ...values,
          organization_id: orgId,
          created_by: profile?.id,
          updated_by: profile?.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

export function useUpdateStaffMember() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (values: { id: string; name: string; role_label?: string }) => {
      const { id, ...updates } = values;
      const { data, error } = await supabase
        .from('staff_members')
        .update({ ...updates, updated_by: profile?.id })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

export function useSetStaffMemberActive() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (values: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('staff_members')
        .update({ is_active: values.is_active, updated_by: profile?.id })
        .eq('id', values.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}
